import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type SubscriptionTier = 'free' | 'pro' | 'premium' | 'legacy';
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'cancelled'
  | 'expired';

export interface Subscription {
  id: number;
  user_id: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  trial_start_date: string | null;
  trial_end_date: string | null;
  created_at: string;
  updated_at: string;
  alertLimit: number;
  alertsRemaining: number;
  watcherCount: number;
  canCreateAlert: boolean;
}

export interface SubscriptionResponse {
  subscription: Subscription;
}

export interface TierInfo {
  name: string;
  price: number;
  alertLimit: number;
  trialDays: number;
}

export interface TiersResponse {
  tiers: Record<SubscriptionTier, TierInfo>;
}

async function fetchSubscription(): Promise<Subscription> {
  const response = await fetch('/api/subscription');
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('UNAUTHORIZED');
    }
    throw new Error('Failed to fetch subscription');
  }
  const data: SubscriptionResponse = await response.json();
  return data.subscription;
}

async function fetchTiers(): Promise<Record<SubscriptionTier, TierInfo>> {
  const response = await fetch('/api/subscription/tiers');
  if (!response.ok) {
    throw new Error('Failed to fetch tiers');
  }
  const data: TiersResponse = await response.json();
  return data.tiers;
}

async function updateSubscription(
  tier: SubscriptionTier
): Promise<Subscription> {
  const response = await fetch('/api/subscription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tier }),
  });
  if (!response.ok) {
    throw new Error('Failed to update subscription');
  }
  const data: SubscriptionResponse = await response.json();
  return data.subscription;
}

async function cancelSubscription(): Promise<Subscription> {
  const response = await fetch('/api/subscription', {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to cancel subscription');
  }
  const data: SubscriptionResponse = await response.json();
  return data.subscription;
}

export function useSubscription(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['subscription'],
    queryFn: fetchSubscription,
    enabled,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors
      if (error instanceof Error && error.message === 'UNAUTHORIZED') {
        return false;
      }
      return failureCount < 3;
    },
  });

  const tier = subscription?.tier ?? 'free';
  const canCreateAlert = subscription?.canCreateAlert ?? false;
  const alertsRemaining = subscription?.alertsRemaining ?? 0;
  const alertLimit = subscription?.alertLimit ?? 0;
  const watcherCount = subscription?.watcherCount ?? 0;
  const isTrialing = subscription?.status === 'trialing';
  const trialEndDate = subscription?.trial_end_date
    ? new Date(subscription.trial_end_date)
    : null;

  // Check if user has a paid tier (pro or premium)
  const hasPaidTier = tier === 'pro' || tier === 'premium';

  // Check if user can access AI alerts (all tiers can access, but with different limits)
  // Free tier has 1 alert, pro has 3, premium has 20, legacy (VIP) has 20
  const canAccessAlerts = alertLimit > 0;

  // Check if user can access graphs (all tiers including free)
  const canAccessGraphs = true;

  return {
    subscription,
    isLoading,
    error,
    refetch,
    tier,
    canCreateAlert,
    alertsRemaining,
    alertLimit,
    watcherCount,
    isTrialing,
    trialEndDate,
    hasPaidTier,
    canAccessAlerts,
    canAccessGraphs,
  };
}

export function useSubscriptionTiers() {
  return useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: fetchTiers,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}
